import {
  isMultiSelect as resolveIsMultiSelect,
  isOtherOptionValue,
  resolveSelectionMode,
  SELECTION_MODES,
  type SelectionMode,
} from '@/shared-question-model';

import type { QuestionSpec } from './shared-responder-v2-core-thread.types';

export { resolveSelectionMode };

export function resolveSpecSelectionMode(spec: QuestionSpec): SelectionMode {
  return resolveSelectionMode(spec.minSelections, spec.maxSelections);
}

/** Thin `QuestionSpec`-shaped wrapper over the shared, bounds-based check. */
export function isMultiSelect(spec: QuestionSpec): boolean {
  return resolveIsMultiSelect(spec.minSelections, spec.maxSelections);
}

/**
 * Applies `maxSelections` when toggling an option on: the oldest selection is
 * dropped rather than the newest rejected, so a responder at the limit can keep
 * changing their mind instead of hitting a dead end.
 *
 * This follows the legacy voice path, which documents the intent as "keep the
 * most recent N distinct options so changing your mind shifts the selection
 * set". Note that the legacy *click* path instead blocks at the limit
 * (`project-response-select.hooks.ts`) — the two disagree, and V2 standardises
 * on drop-oldest for both, matching the prototype.
 *
 * Other is exempt from "oldest first" while `otherText` holds real content:
 * its field has no visible or recoverable state once evicted, so the
 * responder could lose text mid-sentence with no signal. An untyped Other
 * carries nothing to protect and is evicted on the same terms as any pick;
 * eviction still falls back to Other once nothing else is left to drop, so
 * the cap itself is never violated.
 */
export function applySelectionLimit(
  optionValues: readonly string[],
  maxSelections: number | null,
  otherText: string | null
): readonly string[] {
  if (maxSelections === null || optionValues.length <= maxSelections) {
    return optionValues;
  }

  const evictionCount = optionValues.length - maxSelections;
  const otherHoldsText = otherText !== null && otherText.trim() !== '';
  const evictionOrder = otherHoldsText
    ? [
        ...optionValues.filter((value) => !isOtherOptionValue(value)),
        ...optionValues.filter((value) => isOtherOptionValue(value)),
      ]
    : optionValues;
  const evicted = new Set(evictionOrder.slice(0, evictionCount));

  return optionValues.filter((value) => !evicted.has(value));
}

export function limitSpokenSelection(
  optionValues: readonly string[],
  maxSelections: number | null
): readonly string[] {
  return maxSelections === null
    ? optionValues
    : optionValues.slice(0, maxSelections);
}

/**
 * Whether a multi-select set only counts as finished once the responder says so.
 *
 * "At least N" and "up to N" have no natural end — the responder alone knows
 * when the set is complete — so they get a Continue and never auto-advance.
 * "Exactly N" ends at N by definition, so it does the opposite on both counts.
 */
export function awaitsSelectionConfirmation(spec: QuestionSpec): boolean {
  return (
    isMultiSelect(spec) &&
    resolveSpecSelectionMode(spec) !== SELECTION_MODES.Exactly
  );
}

/** Minimum number of selections required before the answer counts as complete. */
export function resolveRequiredSelectionCount(spec: QuestionSpec): number {
  const mode = resolveSpecSelectionMode(spec);

  if (mode === SELECTION_MODES.Exactly) {
    return spec.maxSelections ?? 1;
  }

  if (mode === SELECTION_MODES.AtLeast) {
    return spec.minSelections ?? 1;
  }

  return spec.minSelections ?? 1;
}
